import React from 'react';

import { format } from 'date-fns';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { TokenPeriod } from '@api';

import { formatUsd } from '@shared/helpers/number';

interface AppTokenChartProps {
  data: {
    price: number;
    time: number;
  }[];
  period: TokenPeriod;
}

const getPeriodFormat = (period: TokenPeriod) => {
  if (period === '1d') {
    return 'h:mm a';
  }

  if (period === '7d') {
    return 'dd';
  }

  if (['30d', '3m', '6m', '1y'].includes(period)) {
    return 'dd/MM';
  }

  return 'dd/MM/yy';
};

const AppTokenChart = ({ data, period }: AppTokenChartProps) => {
  const tickFormatter = (time: string) => format(new Date(time), getPeriodFormat(period));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart className="overflow-visible" height={280} data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="time"
          scale="time"
          domain={['auto', 'auto']}
          type="number"
          fontSize={12}
          padding={{ left: 0, right: 34 }}
          minTickGap={period === '1d' ? 5 : 60}
          tick={{ dy: 10 }}
          tickFormatter={tickFormatter}
        />
        <YAxis
          // shows full number and not it's exponential form (1e-23)
          tickFormatter={(value: number) => value?.toLocaleString('en-US', { notation: 'compact' })}
          type="number"
          width={80}
          fontSize={12}
          fontWeight={300}
          domain={['auto', 'auto']}
        />
        <RechartTooltip
          content={({ payload }) => {
            const { price = 0, time = 0 } = payload?.[0]?.payload || {};
            const items = [
              {
                label: 'Date',
                content: time ? format(new Date(time), 'dd/MM/yyyy') : '-',
              },
              {
                label: 'Time',
                content: time ? format(new Date(time), 'dd/MM/yyyy') : '-',
              },
              {
                label: 'Price',
                content: formatUsd(price, { notation: 'compact' }),
              },
            ];

            return (
              <ul className="rounded-lg flex bg-neutral-50 flex-col gap-2 shadow-main p-2.5">
                {items.map((item) => {
                  return (
                    <li key={item.label} className="flex justify-between t-body3 w-full gap-4">
                      <span className="font-medium">{item.label}</span>

                      {item.content}
                    </li>
                  );
                })}
              </ul>
            );
          }}
        />
        <Line type="monotone" dataKey="price" stroke="#4056F7" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default AppTokenChart;
